import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import Block from "../components/Block";
import SectionTeaser from "../components/SectionTeaser";
import { projects, featuredIds } from "../data/projects";
import { useLang, pick } from "../i18n/LanguageContext";
import { t } from "../i18n/translations";

// Typewriter-rytmi kuten Momentumissa. Lauseen jälkeen teksti katoaa
// kerralla ja kursori sykkii kolme kertaa ennen seuraavaa lausetta.
const HOLD_MS = 4200;
const BLINK_GAP_MS = 2700; // 3 × 0.9 s kursorin sykli

const Home = () => {
  const { lang } = useLang();
  const tr = pick(lang);

  // Accent (pinkki osuus) alkaa aina omalta riviltään. Rivinvaihdot
  // ovat kiinteitä — automaattinen rivitys on estetty CSS:ssä.
  const phrases = [
    { lead: tr(t.home.statement), accent: tr(t.home.statementAccent) },
    { lead: tr(t.home.statement2), accent: tr(t.home.statement2Accent) },
    { lead: tr(t.home.statement3), accent: tr(t.home.statement3Accent) },
  ];

  // Ruudunlukija saa kaikki lauseet kerralla eikä kirjoittuvaa välitilaa.
  const statementLabel = phrases
    .map((p) => `${p.lead} ${p.accent}`.replace(/\n/g, " "))
    .join(" ");

  const [display, setDisplay] = useState({ idx: 0, count: 0 });

  useEffect(() => {
    let idx = 0;
    let i = 0;
    let timer;
    const fullOf = (k) => `${phrases[k].lead}\n${phrases[k].accent}`;

    const type = () => {
      const full = fullOf(idx);
      i++;
      setDisplay({ idx, count: i });
      if (i >= full.length) {
        timer = setTimeout(next, HOLD_MS);
        return;
      }
      const ch = full[i - 1];
      const delay =
        ch === " " || ch === "\n"
          ? 90
          : ch === "," || ch === "."
            ? 200
            : 55 + Math.random() * 55;
      timer = setTimeout(type, delay);
    };

    // Teksti pois kerralla, kursori sykkii tyhjässä alussa ennen jatkoa.
    const next = () => {
      idx = (idx + 1) % phrases.length;
      i = 0;
      setDisplay({ idx, count: 0 });
      timer = setTimeout(type, BLINK_GAP_MS);
    };

    timer = setTimeout(type, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const phrase = phrases[display.idx];
  const full = `${phrase.lead}\n${phrase.accent}`;
  const done = display.count >= full.length;

  // Jaetaan kirjoitettu osuus cream-alkuun ja pinkkiin loppuun.
  const leadTyped = full.slice(0, Math.min(display.count, phrase.lead.length + 1));
  const accentTyped =
    display.count > phrase.lead.length + 1
      ? full.slice(phrase.lead.length + 1, display.count)
      : "";

  // Miltton-tyylinen virtaava etusivu: hero → missio → caset → palvelut → CTA.
  // Nostot valitaan käsin projects.js:n featuredIds-listalla; loput /tyot-sivulla.
  const featured = featuredIds
    .map((id) => projects.completed.find((p) => p.id === id))
    .filter(Boolean);

  return (
    <>
      <Hero>
        <h1 className="hero__statement" aria-label={statementLabel}>
          {/* Kaikki lauseet haamuina samassa grid-solussa: laatikko varaa
              leveimmän JA korkeimman lauseen mitat, eikä koskaan elä. */}
          {phrases.map((p, k) => (
            <span key={k} className="hero__statement-ghost" aria-hidden="true">
              {p.lead}
              {"\n"}
              <em>{p.accent}</em>
            </span>
          ))}
          <span className="hero__statement-typed" aria-hidden="true">
            {leadTyped}
            {accentTyped && <em>{accentTyped}</em>}
            <span
              className={`hero__cursor${done ? " hero__cursor--done" : ""}`}
            />
          </span>
        </h1>
      </Hero>

      <Block
        headline={["HETKI"]}
        body={tr(t.home.missionBody)}
        body2={tr(t.home.missionBody2)}
      />

      <SectionTeaser
        title={tr(t.home.workTitle)}
        body={tr(t.home.workBody)}
        linkLabel={tr(t.home.workCta)}
        linkTo="/tyot"
      />

      {featured.map((p, i) => {
        const tp = t.projects[p.id] || {};
        return (
          <Block
            key={p.id}
            id={p.id}
            media={
              p.media && p.media.type === "carousel"
                ? {
                    ...p.media,
                    items: p.media.items.map((item, idx) => ({
                      ...item,
                      label: tp.mediaLabels ? tr(tp.mediaLabels[idx]) : "",
                    })),
                  }
                : p.media && p.media.type === "image" && tp.mediaAlt
                ? { ...p.media, alt: tr(tp.mediaAlt) }
                : p.media
            }
            headline={tr(tp.headline) || p.headline}
            subheadline={tr(tp.subheadline) || p.subheadline}
            body={tr(tp.body) || p.body}
            body2={tr(tp.body2) || p.body2}
            specs={
              p.client
                ? [
                    { label: tr(t.tyot.specClient), value: p.client },
                    { label: tr(t.tyot.specYear), value: p.year },
                  ]
                : undefined
            }
          />
        );
      })}

      <SectionTeaser
        title={tr(t.home.servicesTitle)}
        body={tr(t.home.servicesBody)}
        linkLabel={tr(t.home.servicesCta)}
        linkTo="/palvelut"
      />

      <section className="cta-band">
        <p className="cta-band__hook">{tr(t.home.contactHook)}</p>
        <Link to="/yhteys" className="cta-band__link">
          {tr(t.home.contactCta)}
          <span className="cta-band__arrows" aria-hidden="true">
            → → →
          </span>
        </Link>
      </section>
    </>
  );
};

export default Home;
